/**
 * RecentScreen - Conversation History
 * 
 * Displays user's conversation history fetched from Firestore.
 * Supports pinning, deleting, and searching conversations.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { getMessages } from '../services/conversations';
import {
  SpaceBackground,
  Header,
  ChatBar,
  BottomNav,
  Sidebar,
  SettingsPanel,
  PageTitle,
  GlassCard,
  AtomIcon,
  Search,
  Pin,
  Trash2,
  FolderPlus,
  MoreVertical,
  ChevronRight,
  X,
  Clock,
  Activity,
  Loader
} from '../components/shared';

interface Conversation {
  id: string;
  title: string;
  preview: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  pinned: boolean;
  messageCount: number;
}

// Helper function to format date for display
function formatConversationDate(timestamp: Timestamp): { date: string; time: string } {
  const date = timestamp.toDate();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const conversationDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  if (conversationDate.getTime() === today.getTime()) {
    return { date: 'Today', time: timeStr };
  } else if (conversationDate.getTime() === yesterday.getTime()) {
    return { date: 'Yesterday', time: timeStr };
  } else {
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return { date: dateStr, time: timeStr };
  }
}

// Full-screen modal menu for conversation actions
const ActionModal = ({
  isOpen,
  onClose,
  conversation,
  onPin,
  onDelete,
  onMoveToProject,
  onAddToTasks
}: {
  isOpen: boolean;
  onClose: () => void;
  conversation: Conversation | null;
  onPin: () => void;
  onDelete: () => void;
  onMoveToProject: () => void;
  onAddToTasks: () => void;
}) => {
  if (!isOpen || !conversation) return null;

  const { date, time } = formatConversationDate(conversation.updatedAt);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-sm mx-4 bg-black/95 backdrop-blur-2xl border border-cyan-400/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
        {/* Header */}
        <div className="p-4 border-b border-cyan-400/20">
          <h3 className="text-cyan-300 font-semibold text-lg truncate">{conversation.title}</h3>
          <p className="text-cyan-400/50 text-sm mt-1">{date} at {time}</p>
        </div>

        {/* Actions */}
        <div className="p-2">
          <button
            onClick={() => { onPin(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <Pin className={`w-5 h-5 ${conversation.pinned ? 'fill-cyan-400' : ''}`} />
            <span>{conversation.pinned ? 'Unpin from top' : 'Pin to top'}</span>
          </button>
          <button
            onClick={() => { onAddToTasks(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <Activity className="w-5 h-5" />
            <span>Add to My Tasks</span>
          </button>
          <button
            onClick={() => { onMoveToProject(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <FolderPlus className="w-5 h-5" />
            <span>Move to project</span>
          </button>
          <button
            onClick={() => { onDelete(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-red-400/90 hover:bg-red-400/10 hover:text-red-300 flex items-center space-x-4 rounded-xl transition-all border-t border-cyan-400/10 mt-2"
          >
            <Trash2 className="w-5 h-5" />
            <span>Delete conversation</span>
          </button>
        </div>

        {/* Cancel button */}
        <div className="p-4 border-t border-cyan-400/20">
          <button
            onClick={onClose}
            className="w-full py-3 text-cyan-400 font-semibold bg-cyan-400/10 hover:bg-cyan-400/20 rounded-xl transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const ConversationCard = ({
  conversation,
  onMenuOpen,
  onClick,
  style
}: {
  conversation: Conversation;
  onMenuOpen: () => void;
  onClick: () => void;
  style?: React.CSSProperties;
}) => {
  const { date, time } = formatConversationDate(conversation.updatedAt);

  return (
    <div style={style}>
      <GlassCard
        className="p-4 cursor-pointer hover:scale-[1.01] transition-transform"
        onClick={onClick}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              {conversation.pinned && (
                <Pin className="w-4 h-4 text-cyan-400 fill-cyan-400 shrink-0" />
              )}
              <h3 className="text-cyan-300 font-semibold text-base truncate drop-shadow-[0_0_3px_rgba(34,211,238,0.3)]">
                {conversation.title}
              </h3>
            </div>
            <p className="text-cyan-400/50 text-sm mt-1 truncate">
              {conversation.preview}
            </p>
            <p className="text-cyan-400/30 text-xs mt-2">
              {date} at {time}
            </p>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={(e) => { e.stopPropagation(); onMenuOpen(); }}
              className="p-2 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            <ChevronRight className="w-5 h-5 text-cyan-400/40" />
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

// Conversation Detail View
const ConversationDetailView = ({ conversation, onClose }: { conversation: Conversation; onClose: () => void }) => {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [messages, setMessages] = useState<Array<{ role: string; message: string }>>([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const { user } = useAuthContext();
  const { date, time } = formatConversationDate(conversation.updatedAt);

  // Fetch actual messages from Firestore
  useEffect(() => {
    const fetchMessages = async () => {
      if (!user?.uid) return;

      setLoadingMessages(true);
      try {
        const conversationMessages = await getMessages(user.uid, conversation.id);
        if (conversationMessages.length > 0) {
          setMessages(conversationMessages.map(m => ({
            role: m.role,
            message: m.content || ''
          })));
        } else {
          // Fallback to preview if no messages found
          setMessages([
            { role: 'user', message: conversation.title },
            { role: 'assistant', message: conversation.preview },
          ]);
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
        // Fallback on error
        setMessages([
          { role: 'user', message: conversation.title },
          { role: 'assistant', message: conversation.preview },
        ]);
      }
      setLoadingMessages(false);
    };

    fetchMessages();
  }, [user?.uid, conversation.id, conversation.title, conversation.preview]);

  return (
    <div className="fixed inset-0 z-[70] bg-black">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <header className="p-6 flex items-center border-b border-cyan-400/10">
          <button
            onClick={onClose}
            className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-6"
          >
            <X className="w-6 h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
          </button>

          <div className="flex items-center space-x-3">
            <AtomIcon className="w-10 h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
            <div>
              <h1 className="text-xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">
                {conversation.title}
              </h1>
              <p className="text-cyan-400/50 text-xs mt-0.5">{date} at {time}</p>
            </div>
          </div>

          <div className="ml-auto">
            <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loadingMessages ? (
            <div className="flex items-center justify-center py-8">
              <AtomIcon className="w-8 h-8 text-cyan-400 animate-spin-slow" />
              <span className="ml-2 text-cyan-400/60 text-sm">Loading messages...</span>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] p-4 rounded-2xl ${msg.role === 'user'
                    ? 'bg-cyan-400/20 border border-cyan-400/40'
                    : 'bg-cyan-500/[0.05] border border-cyan-400/20'
                  }`}>
                  <p className="text-cyan-300 text-sm">{msg.message}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Chat Bar */}
        <div className="p-4 flex flex-col items-center">
          <ChatBar
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={() => {
              if (inputText.trim()) {
                navigate(`/?convId=${conversation.id}&message=${encodeURIComponent(inputText.trim())}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Empty state component
const EmptyState = ({ isLoading }: { isLoading: boolean }) => {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader className="w-12 h-12 text-cyan-400/40 mx-auto mb-4 animate-spin" />
        <p className="text-cyan-400/50">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <Clock className="w-12 h-12 text-cyan-400/20 mx-auto mb-4" />
      <p className="text-cyan-400/50 mb-2">No conversations yet</p>
      <p className="text-cyan-400/30 text-sm">Start a chat to see your history here</p>
    </div>
  );
};

export default function RecentScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [searchQuery, setSearchQuery] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [menuConversation, setMenuConversation] = useState<Conversation | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch conversations from Firestore
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const conversationsRef = collection(db, 'superjarvis_users', user.uid, 'conversations');
    const q = query(
      conversationsRef,
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs: Conversation[] = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title || 'Untitled conversation',
        preview: doc.data().preview || doc.data().lastMessage || '',
        createdAt: doc.data().createdAt || Timestamp.now(),
        updatedAt: doc.data().updatedAt || doc.data().createdAt || Timestamp.now(),
        pinned: doc.data().pinned || false,
        messageCount: doc.data().messageCount || 0
      }));
      setConversations(convs);
      setIsLoading(false);
    }, (error) => {
      console.error('[RecentScreen] Error fetching conversations:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.preview.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group conversations by date
  const pinnedConversations = filteredConversations.filter(c => c.pinned);

  const todayConversations = filteredConversations.filter(c => {
    if (c.pinned) return false;
    const { date } = formatConversationDate(c.updatedAt);
    return date === 'Today';
  });

  const yesterdayConversations = filteredConversations.filter(c => {
    if (c.pinned) return false;
    const { date } = formatConversationDate(c.updatedAt);
    return date === 'Yesterday';
  });

  const olderConversations = filteredConversations.filter(c => {
    if (c.pinned) return false;
    const { date } = formatConversationDate(c.updatedAt);
    return date !== 'Today' && date !== 'Yesterday';
  });

  const handlePin = async (id: string) => {
    if (!user) return;
    const conv = conversations.find(c => c.id === id);
    if (!conv) return;

    try {
      const convRef = doc(db, 'superjarvis_users', user.uid, 'conversations', id);
      await updateDoc(convRef, { pinned: !conv.pinned });
    } catch (error) {
      console.error('[RecentScreen] Error updating pin status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!user) return;

    try {
      const convRef = doc(db, 'superjarvis_users', user.uid, 'conversations', id);
      await deleteDoc(convRef);
    } catch (error) {
      console.error('[RecentScreen] Error deleting conversation:', error);
    }
  };

  const handleMoveToProject = (id: string) => {
    const conv = conversations.find(c => c.id === id);
    if (!conv) return;

    // Navigate to projects to link conversation
    navigate('/projects', { state: { linkConversation: conv } });
  };

  const handleAddToTasks = async (id: string) => {
    const conv = conversations.find(c => c.id === id);
    if (!conv || !user) return;

    try {
      // Import and use tasksApi to create a task from the conversation
      const { createTask } = await import('../services/tasksApi');
      await createTask({
        user_id: user.uid,
        title: conv.title,
        description: `Task created from conversation: ${conv.preview}`,
        metadata: {
          source: 'conversation',
          conversationId: id,
          frequency: 'once'
        }
      });
      alert(`Task created: "${conv.title}"`);
    } catch (error) {
      console.error('[RecentScreen] Error creating task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  const handleConversationClick = (conversation: Conversation) => {
    // Navigate to HomeScreen with the conversation ID (new UI)
    navigate(`/?conversationId=${conversation.id}`);
  };

  // Map UI mode labels to API execution mode values
  const getExecutionMode = (mode: string): string => {
    switch (mode) {
      case 'Fast': return 'fast';
      case 'Super': return 'super';
      case 'Super Think': return 'super_think';
      default: return 'super';  // SUPER is the default mode
    }
  };

  const handleChatSubmit = () => {
    if (inputText.trim()) {
      const executionMode = getExecutionMode(chatMode);
      navigate(`/?message=${encodeURIComponent(inputText.trim())}&mode=${executionMode}`);
    }
  };

  if (selectedConversation) {
    return <ConversationDetailView conversation={selectedConversation} onClose={() => setSelectedConversation(null)} />;
  }

  // Calculate the max scroll before items should start fading (distance from chat bar)
  const chatBarOffset = 240; // Height of bottom section (chat bar + nav)

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Content Area with scroll fade */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex flex-col justify-start pt-28 px-4 overflow-y-auto w-full items-center"
          style={{
            paddingBottom: chatBarOffset,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))'
          }}
        >
          <div className="w-full max-w-3xl">
            <PageTitle title="Recent" />

            {/* Search Bar */}
            <div className="mt-4 mb-6">
              <GlassCard className="flex items-center px-4 py-3">
                <Search className="w-5 h-5 text-cyan-400/50 mr-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-base font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-1 rounded-full hover:bg-cyan-400/10 text-cyan-400/50 hover:text-cyan-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </GlassCard>
            </div>

            {/* Conversations List */}
            <div className="space-y-6">
              {/* Loading or Empty State */}
              {(isLoading || filteredConversations.length === 0) && (
                <EmptyState isLoading={isLoading} />
              )}

              {/* Pinned */}
              {pinnedConversations.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest mb-3 flex items-center space-x-2">
                    <Pin className="w-4 h-4 fill-cyan-400/50" />
                    <span>Pinned</span>
                  </h3>
                  <div className="space-y-3">
                    {pinnedConversations.map(conv => (
                      <ConversationCard
                        key={conv.id}
                        conversation={conv}
                        onMenuOpen={() => setMenuConversation(conv)}
                        onClick={() => handleConversationClick(conv)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Today */}
              {todayConversations.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest mb-3 flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>Today</span>
                  </h3>
                  <div className="space-y-3">
                    {todayConversations.map(conv => (
                      <ConversationCard
                        key={conv.id}
                        conversation={conv}
                        onMenuOpen={() => setMenuConversation(conv)}
                        onClick={() => handleConversationClick(conv)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Yesterday */}
              {yesterdayConversations.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest mb-3">Yesterday</h3>
                  <div className="space-y-3">
                    {yesterdayConversations.map(conv => (
                      <ConversationCard
                        key={conv.id}
                        conversation={conv}
                        onMenuOpen={() => setMenuConversation(conv)}
                        onClick={() => handleConversationClick(conv)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Older */}
              {olderConversations.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest mb-3">Older</h3>
                  <div className="space-y-3">
                    {olderConversations.map(conv => (
                      <ConversationCard
                        key={conv.id}
                        conversation={conv}
                        onMenuOpen={() => setMenuConversation(conv)}
                        onClick={() => handleConversationClick(conv)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleChatSubmit}
          />
          <BottomNav activeTab="recent" />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />

      {/* Action Modal */}
      <ActionModal
        isOpen={!!menuConversation}
        onClose={() => setMenuConversation(null)}
        conversation={menuConversation}
        onPin={() => menuConversation && handlePin(menuConversation.id)}
        onDelete={() => menuConversation && handleDelete(menuConversation.id)}
        onMoveToProject={() => menuConversation && handleMoveToProject(menuConversation.id)}
        onAddToTasks={() => menuConversation && handleAddToTasks(menuConversation.id)}
      />
    </div>
  );
}
